/* VoxelPals: a tiny, user-initiated loader for the local Three.js showroom. */
(() => {
  'use strict';
  if (window.__vpShowroomController) return;
  const runtimes = new Map();
  const cancelRuntimes = new Set();
  const instances = new Set();
  const cleanup = [];
  let disposed = false;
  let suspended = false;
  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    cleanup.push(() => target.removeEventListener(type, handler, options));
  };

  function runtime(url) {
    if (window.VoxelPalsShowroom) return Promise.resolve(window.VoxelPalsShowroom);
    if (runtimes.has(url)) return runtimes.get(url);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout); cancelRuntimes.delete(cancel);
        script.onload = script.onerror = null;
        if (error) { script.remove(); runtimes.delete(url); reject(error); }
        else resolve(window.VoxelPalsShowroom);
      };
      const cancel = () => finish(new Error('The page left the interactive preview.'));
      const timeout = setTimeout(() => finish(new Error('The 3D viewer took too long to load.')), 25000);
      cancelRuntimes.add(cancel);
      script.onload = () => {
        finish(window.VoxelPalsShowroom ? null : new Error('The 3D viewer could not start.'));
      };
      script.onerror = () => {
        finish(new Error('The 3D viewer could not load. Please try again.'));
      };
      document.head.appendChild(script);
    });
    runtimes.set(url, promise);
    return promise;
  }

  function init(root) {
    if (root.dataset.showroomInitialized) return;
    if ('showroomTheatre' in root.dataset) { initTheatre(root); return; }
    const stage = root.querySelector('[data-showroom-stage]');
    const loadButton = root.querySelector('[data-showroom-load]');
    const poster = root.querySelector('[data-showroom-poster]');
    const status = root.querySelector('[data-showroom-status]');
    const choices = Array.from(root.querySelectorAll('[data-showroom-model]'));
    if (!stage || !loadButton || !root.querySelector('[data-showroom-canvas]')) return;
    root.dataset.showroomInitialized = 'true';
    root.dataset.showroomState = 'poster';
    root.dataset.showroomInteracting = 'false';
    stage.tabIndex = -1;
    stage.setAttribute('aria-label', 'Model preview. Interactive controls follow below.');
    if (status) { status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); status.setAttribute('aria-atomic', 'true'); }
    const message = (text) => { if (status) status.textContent = text; };
    let toolbar = root.querySelector('[data-showroom-controls]');
    if (!toolbar) {
      toolbar = document.createElement('div');
      toolbar.className = 'showroom__controls';
      toolbar.setAttribute('data-showroom-controls', '');
      toolbar.hidden = true;
      stage.insertAdjacentElement('afterend', toolbar);
    }
    toolbar.innerHTML = '<div class="showroom__tools" role="group" aria-label="Model view controls">' +
      '<button type="button" data-showroom-action="interact" aria-pressed="false">Interact with model</button>' +
      '<button type="button" data-showroom-action="left" aria-label="Rotate model left">↶</button>' +
      '<button type="button" data-showroom-action="right" aria-label="Rotate model right">↷</button>' +
      '<button type="button" data-showroom-action="zoom-in" aria-label="Zoom in">＋</button>' +
      '<button type="button" data-showroom-action="zoom-out" aria-label="Zoom out">−</button>' +
      '<button type="button" data-showroom-action="reset">Reset view</button>' +
      '<button type="button" data-showroom-action="rotate" aria-pressed="false">Auto-rotate</button></div>' +
      '<div class="showroom__lights" role="group" aria-label="Studio lighting">' +
      '<span>Lighting</span><button type="button" data-showroom-light="neutral" aria-pressed="true">Neutral</button>' +
      '<button type="button" data-showroom-light="warm" aria-pressed="false">Warm</button>' +
      '<button type="button" data-showroom-light="studio" aria-pressed="false">Cool</button></div>';
    const interactButton = toolbar.querySelector('[data-showroom-action="interact"]');
    const rotateButton = toolbar.querySelector('[data-showroom-action="rotate"]');
    let selected = choices.find((button) => button.getAttribute('aria-pressed') === 'true') || choices[0];
    let view = null;
    let loading = false;
    let currentSequence = 0;
    let isInteracting = false;
    let everLoaded = false;
    function yaw() {
      const value = selected?.dataset.modelYaw ?? root.dataset.modelYaw ?? selected?.dataset.modelRotation ?? root.dataset.modelRotation ?? '0';
      const numeric = parseFloat(value) || 0;
      return String(value).trim().endsWith('rad') ? numeric * 180 / Math.PI : numeric;
    }

    function choose(button) {
      selected = button;
      choices.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      if (poster && button?.dataset.modelPoster) poster.src = button.dataset.modelPoster;
      if (poster && button?.dataset.modelLabel) poster.alt = `${button.dataset.modelLabel} — static preview`;
      const label = root.querySelector('[data-showroom-model-name]');
      if (label && button?.dataset.modelLabel) label.textContent = button.dataset.modelLabel;
    }
    if (selected) choose(selected);
    function setInteract(value) {
      isInteracting = Boolean(value);
      view?.interact(isInteracting);
      interactButton.setAttribute('aria-pressed', String(isInteracting));
      interactButton.textContent = isInteracting ? 'Done · scroll page' : 'Interact with model';
      if (isInteracting) message('Drag to rotate · pinch or scroll to zoom. Press Done or Escape to return to page scrolling.');
      else if (view && everLoaded && !loading) message('Model ready. Interact to drag and pinch, or use the view controls.');
    }
    function failed(error) {
      if (disposed) return;
      currentSequence++;
      root.dataset.showroomState = 'error';
      root.setAttribute('aria-busy', 'false');
      loading = false;
      toolbar.hidden = true;
      loadButton.hidden = false;
      loadButton.disabled = false;
      loadButton.textContent = 'Retry interactive model';
      choices.forEach(button => { button.disabled = false; });
      message(error.message || String(error));
      view?.dispose(); view = null;
      isInteracting = false;
      root.dataset.showroomInteracting = 'false';
    }
    async function load() {
      if (loading || disposed || suspended) return;
      const src = selected?.dataset.modelSrc || root.dataset.modelSrc;
      const runtimeUrl = root.dataset.showroomRuntime;
      if (!src || !runtimeUrl) { failed(new Error('This model preview is not configured yet.')); return; }
      if (!window.IntersectionObserver || !window.ResizeObserver) {
        failed(new Error('This browser can show the image preview but does not support the interactive viewer.'));
        return;
      }
      const sequence = ++currentSequence;
      const restoreFocus = document.activeElement === loadButton;
      loading = true;
      setInteract(false);
      root.dataset.showroomState = 'loading';
      root.setAttribute('aria-busy', 'true');
      toolbar.hidden = true;
      loadButton.disabled = true;
      loadButton.hidden = false;
      loadButton.textContent = 'Preparing 3D…';
      choices.forEach(button => { button.disabled = true; });
      message('Starting the interactive viewer. Images stay available while it loads.');
      try {
        const engine = await runtime(runtimeUrl);
        if (disposed || suspended || sequence !== currentSequence) return;
        if (!view) view = engine.create(root, {
          progress: message,
          inactive: () => setInteract(false),
          rotateStopped: () => rotateButton.setAttribute('aria-pressed', 'false'),
          error: (text) => failed(new Error(text))
        });
        await view.load(src, yaw());
        if (disposed || suspended || sequence !== currentSequence) return;
        loading = false;
        everLoaded = true;
        root.dataset.showroomState = 'ready';
        root.setAttribute('aria-busy', 'false');
        loadButton.disabled = false;
        loadButton.hidden = true;
        toolbar.hidden = false;
        choices.forEach(button => { button.disabled = false; });
        rotateButton.setAttribute('aria-pressed', 'false');
        view.rotate(false);
        toolbar.querySelectorAll('[data-showroom-light]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.showroomLight === 'neutral')));
        view.lighting('neutral');
        message('Model ready. Interact to drag and pinch, or use the view controls.');
        // Keep focus in the same display area; focusing the newly inserted toolbar
        // can otherwise make sticky headers obscure the model on narrow screens.
        if (restoreFocus) stage.focus({ preventScroll: true });
      } catch (error) { if (sequence === currentSequence) failed(error); }
    }
    listen(loadButton, 'click', load);
    choices.forEach((button) => listen(button, 'click', () => {
      if (loading || button === selected) return;
      choose(button);
      if (everLoaded) load();
      else message('Sample selected. Load the interactive model to explore it in 3D.');
    }));
    listen(toolbar, 'click', (event) => {
      if (!view || loading) return;
      // The root also exposes data-showroom-light as observable state.
      // Only actual light buttons should consume toolbar clicks.
      const light = event.target.closest('button[data-showroom-light]');
      if (light) {
        toolbar.querySelectorAll('[data-showroom-light]').forEach((button) => button.setAttribute('aria-pressed', String(button === light)));
        view.lighting(light.dataset.showroomLight);
        return;
      }
      const action = event.target.closest('[data-showroom-action]')?.dataset.showroomAction;
      if (action === 'interact') setInteract(!isInteracting);
      if (action === 'reset') view.reset();
      if (action === 'left') view.turn(-20);
      if (action === 'right') view.turn(20);
      if (action === 'zoom-in') view.zoom(0.85);
      if (action === 'zoom-out') view.zoom(1.18);
      if (action === 'rotate') {
        const enabled = view.rotate(rotateButton.getAttribute('aria-pressed') !== 'true');
        rotateButton.setAttribute('aria-pressed', String(enabled));
        if (!enabled && window.matchMedia('(prefers-reduced-motion: reduce)').matches) message('Auto-rotate stays off because your device prefers reduced motion. Use the rotate buttons instead.');
      }
    });
    listen(root, 'keydown', (event) => {
      if (event.key === 'Escape' && isInteracting) { setInteract(false); interactButton.focus({ preventScroll: true }); }
    });
    instances.add({
      suspend: () => {
        if (loading) {
          currentSequence++; loading = false;
          view?.dispose(); view = null;
          root.dataset.showroomState = 'poster';
          root.setAttribute('aria-busy', 'false');
          toolbar.hidden = true;
          loadButton.hidden = false; loadButton.disabled = false;
          loadButton.textContent = 'Load interactive model';
          choices.forEach(button => { button.disabled = false; });
          message('Image preview available. Load the interactive model when you return.');
        }
        setInteract(false);
        view?.rotate(false);
        rotateButton.setAttribute('aria-pressed', 'false');
      },
      dispose: () => {
        currentSequence++; view?.dispose(); view = null;
        delete root.dataset.showroomInitialized;
        root.dataset.showroomGpu = 'disposed';
      }
    });
  }
  // Full-screen theatre uses the same lazy engine and lifecycle as embedded previews,
  // but direct manipulation is the default, never a row of rotate/zoom buttons.
  function initTheatre(root) {
    const stage = root.querySelector('[data-showroom-stage]');
    const loadButton = root.querySelector('[data-showroom-load]');
    const poster = root.querySelector('[data-showroom-poster]');
    const status = root.querySelector('[data-showroom-status]');
    const choices = Array.from(root.querySelectorAll('[data-showroom-model]'));
    const controls = root.querySelector('[data-showroom-controls]');
    const interactButton = root.querySelector('[data-theatre-interact]');
    const arrows = Array.from(root.querySelectorAll('[data-theatre-step]'));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!stage || !loadButton || !choices.length) return;
    root.dataset.showroomInitialized = 'true';
    root.dataset.showroomState = 'closed';
    root.dataset.theatreCurtain = 'closed';
    stage.tabIndex = 0;
    stage.setAttribute('aria-label', 'Interactive figurine stage. Drag to rotate; scroll or pinch to zoom. Arrow keys rotate. Plus and minus zoom. Escape releases the model.');
    let selectedIndex = Math.max(0, choices.findIndex(button => button.getAttribute('aria-pressed') === 'true'));
    let view = null;
    let sequence = 0;
    let loading = false;
    let opened = false;
    let interacting = false;
    let interactiveIntent = true;
    let timer = 0;
    let light = 'neutral';
    let backdrop = 'midnight';
    const timers = new Map();
    const wait = ms => new Promise(resolve => { const id = setTimeout(() => { timers.delete(id); resolve(); }, reduce.matches ? 0 : ms); timers.set(id, resolve); });
    const message = text => { if (status) status.textContent = text; };
    const ownWorldVisible = () => !root.dataset.world || !document.documentElement.dataset.world || root.dataset.world === document.documentElement.dataset.world;
    const setInteract = (value, remember = false) => {
      if (remember) interactiveIntent = Boolean(value);
      interacting = Boolean(value) && !loading && !document.hidden && ownWorldVisible();
      view?.interact(interacting);
      root.dataset.showroomInteracting = String(interacting);
      if (interactButton) {
        interactButton.setAttribute('aria-pressed', String(interacting));
        interactButton.textContent = interacting ? 'Pause touch control' : 'Resume touch control';
      }
    };
    function syncArrows() {
      const singleModel = choices.length < 2;
      // Opacity and pointer-events do not remove hidden controls from keyboard
      // navigation. Keep arrows disabled until the open stage is usable, including
      // the outgoing and incoming model animations; errors still allow another model.
      const available = opened && !loading && !disposed && root.dataset.theatreCurtain === 'open' && !root.dataset.theatreSlide;
      arrows.forEach(button => { button.hidden = singleModel; button.disabled = singleModel || !available; });
    }
    function choose(index) {
      selectedIndex = (index + choices.length) % choices.length;
      const selected = choices[selectedIndex];
      choices.forEach((button, i) => button.setAttribute('aria-pressed', String(i === selectedIndex)));
      if (poster) { poster.src = selected.dataset.modelPoster; poster.alt = `${selected.dataset.modelLabel} — technical sample, not approved merchandise`; }
      const name = root.querySelector('[data-showroom-model-name]');
      const counter = root.querySelector('[data-theatre-counter]');
      if (name) name.textContent = selected.dataset.modelLabel;
      if (counter) counter.textContent = `${String(selectedIndex + 1).padStart(2, '0')} / ${String(choices.length).padStart(2, '0')}`;
      const brief = root.querySelector('[data-theatre-customise]');
      if (brief && selected.dataset.modelCta) brief.href = selected.dataset.modelCta;
      syncArrows();
    }
    choose(selectedIndex);
    function fail(error) {
      if (disposed) return;
      sequence++; loading = false;
      root.dataset.showroomState = 'error';
      root.dataset.theatreCurtain = 'open';
      root.dataset.theatreSlide = '';
      syncArrows();
      root.setAttribute('aria-busy', 'false');
      loadButton.hidden = false; loadButton.disabled = false;
      loadButton.textContent = 'Retry interactive model';
      if (controls) controls.hidden = true;
      setInteract(false);
      view?.dispose(); view = null;
      message(error.message || 'The interactive model is unavailable. The image preview remains available.');
    }
    async function reveal(direction = 0) {
      if (disposed || suspended || !ownWorldVisible()) return;
      const current = ++sequence;
      const selected = choices[selectedIndex];
      loading = true;
      syncArrows();
      setInteract(false);
      root.setAttribute('aria-busy', 'true');
      loadButton.disabled = true;
      // 一点下去就把按钮收掉，别把它变成「Preparing your stage…」胶囊挂在正中间：
      // 运行时 636KB + 模型最大 2.2MB，那个胶囊会把整段等待明晃晃摆给客人看。
      // 帘幕动画和海报图本身就是完整画面，等待被它们盖住即可。
      // 失败路径不受影响：fail() 会把按钮放回来并改成 Retry interactive model。
      loadButton.hidden = true;
      if (controls) controls.hidden = true;
      if (!opened) {
        opened = true;
        root.dataset.theatreCurtain = 'opening';
      }
      const curtain = root.dataset.theatreCurtain === 'opening' ? wait(1050) : Promise.resolve();
      try {
        if (!window.IntersectionObserver || !window.ResizeObserver) throw new Error('This browser supports the image preview, but not interactive 3D.');
        if (direction && root.dataset.showroomState === 'ready') {
          root.dataset.theatreSlide = direction > 0 ? 'exit-left' : 'exit-right';
          await wait(220);
        }
        if (disposed || suspended || current !== sequence) return;
        root.dataset.showroomState = 'loading';
        root.dataset.theatreSlide = '';
        message('Preparing your stage…');
        const engine = await runtime(root.dataset.showroomRuntime);
        if (disposed || suspended || current !== sequence) return;
        if (!view) view = engine.create(root, {
          progress: text => { if (loading) message(text); },
          inactive: () => setInteract(false),
          active: () => { if (!loading && interactiveIntent) setInteract(true); },
          error: text => fail(new Error(text))
        });
        const rawYaw = selected.dataset.modelYaw ?? root.dataset.modelYaw ?? '0';
        const yaw = parseFloat(rawYaw) || 0;
        await view.load(selected.dataset.modelSrc, String(rawYaw).endsWith('rad') ? yaw * 180 / Math.PI : yaw);
        await curtain;
        if (disposed || suspended || current !== sequence) return;
        root.dataset.theatreCurtain = 'open';
        root.dataset.showroomState = 'ready';
        root.dataset.theatreSlide = direction > 0 ? 'enter-right' : direction < 0 ? 'enter-left' : '';
        loading = false;
        syncArrows();
        root.setAttribute('aria-busy', 'false');
        loadButton.hidden = true; loadButton.disabled = false;
        if (controls) controls.hidden = false;
        view.rotate(false);
        view.lighting(light);
        view.background?.(backdrop);
        setInteract(interactiveIntent);
        message('Drag to rotate · scroll or pinch to zoom.');
        if (!direction) stage.focus({ preventScroll: true });
        clearTimeout(timer);
        timer = setTimeout(() => { if (current === sequence) { root.dataset.theatreSlide = ''; syncArrows(); } }, reduce.matches ? 0 : 380);
      } catch (error) { if (current === sequence) fail(error); }
    }
    listen(loadButton, 'click', () => reveal());
    // Clicking the fabric is equivalent to clicking its keyboard-accessible open button.
    const curtainHit = root.querySelector('[data-theatre-open]');
    if (curtainHit) listen(curtainHit, 'click', () => { if (!opened) reveal(); });
    arrows.forEach(button => listen(button, 'click', () => {
      if (button.disabled || !opened || loading || choices.length < 2) return;
      const direction = Number(button.dataset.theatreStep) || 1;
      choose(selectedIndex + direction); reveal(direction);
    }));
    choices.forEach((button, index) => listen(button, 'click', () => {
      if (loading || index === selectedIndex) return;
      const direction = index > selectedIndex ? 1 : -1;
      choose(index); if (opened) reveal(direction);
    }));
    if (controls) listen(controls, 'click', event => {
      if (!view || loading) return;
      const lighting = event.target.closest('button[data-showroom-light]');
      const background = event.target.closest('button[data-theatre-background]');
      if (lighting) {
        light = lighting.dataset.showroomLight;
        controls.querySelectorAll('[data-showroom-light]').forEach(button => button.setAttribute('aria-pressed', String(button === lighting)));
        view.lighting(light);
      } else if (background) {
        backdrop = background.dataset.theatreBackground;
        controls.querySelectorAll('[data-theatre-background]').forEach(button => button.setAttribute('aria-pressed', String(button === background)));
        root.dataset.theatreBackground = backdrop;
        view.background?.(backdrop);
      } else if (event.target.closest('[data-theatre-reset]')) view.reset();
      else if (event.target.closest('[data-theatre-interact]')) setInteract(!interacting, true);
    });
    listen(stage, 'keydown', event => {
      if (!view || loading || event.target !== stage) return;
      if (event.key === 'Escape') {
        setInteract(false, true);
        // Do not send keyboard focus into a collapsed native details element.
        root.querySelector('.theatre__options')?.setAttribute('open', '');
        interactButton?.focus({ preventScroll: true });
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); view.turn(event.key === 'ArrowLeft' ? -20 : 20); }
      if (event.key === '+' || event.key === '=') { event.preventDefault(); view.zoom(.85); }
      if (event.key === '-') { event.preventDefault(); view.zoom(1.18); }
      if (event.key === 'Home') { event.preventDefault(); view.reset(); }
    });
    listen(document, 'visibilitychange', () => { if (!loading && view) setInteract(!document.hidden && interactiveIntent); });
    listen(window, 'pageshow', () => { if (!loading && view) setInteract(interactiveIntent); });
    listen(window, 'vp:worldchange', () => { if (!loading && view) setInteract(ownWorldVisible() && interactiveIntent); });
    listen(reduce, 'change', () => { if (reduce.matches) { timers.forEach((resolve, id) => { clearTimeout(id); resolve(); }); timers.clear(); root.dataset.theatreSlide = ''; syncArrows(); } });
    instances.add({
      suspend: () => {
        if (loading) { sequence++; loading = false; view?.dispose(); view = null; root.dataset.showroomState = 'error'; root.dataset.theatreCurtain = 'open'; root.dataset.theatreSlide = ''; root.setAttribute('aria-busy', 'false'); loadButton.hidden = false; loadButton.disabled = false; loadButton.textContent = 'Resume interactive model'; syncArrows(); }
        setInteract(false);
      },
      dispose: () => { sequence++; clearTimeout(timer); timers.forEach((resolve, id) => { clearTimeout(id); resolve(); }); timers.clear(); view?.dispose(); view = null; delete root.dataset.showroomInitialized; root.dataset.showroomGpu = 'disposed'; syncArrows(); }
    });
  }
  const boot = () => { if (!disposed) document.querySelectorAll('[data-showroom]').forEach(init); };
  function dispose() {
    if (disposed) return;
    disposed = true;
    instances.forEach(instance => instance.dispose()); instances.clear();
    cancelRuntimes.forEach(cancel => cancel()); cancelRuntimes.clear(); runtimes.clear();
    cleanup.splice(0).forEach(fn => fn());
    delete window.__vpShowroomController;
  }
  window.__vpShowroomController = { dispose };
  if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', boot, { once: true }); else boot();
  // Retain a ready viewer in BFCache, but invalidate any unfinished async creation.
  listen(window, 'pagehide', event => {
    if (!event.persisted) { dispose(); return; }
    suspended = true; instances.forEach(instance => instance.suspend());
  });
  listen(window, 'pageshow', () => { suspended = false; });
})();
